import { COLOR_GRAY_13 } from 'shared/helpers/colors';
import styled from 'styled-components';
import type { EventProps } from './types';
import useWebsiteEditor from 'shared/hooks/useWebsiteEditor';
import {
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText,
  shade,
  generateAccessKey
} from 'shared/helpers/index';
import useSales from 'shared/hooks/useSales';
import { useMemo } from 'react';
import useIsEmpty from 'shared/hooks/useIsEmpty';

import SvgIcons from '../../Common/IconsEvent';

const Container = styled.div`
  position: relative;
  background: #fff;
  padding: 70px 0 0px;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    padding: 60px 0;
  `}
`;

const SaleContainer = styled.div`
  width: 1254px;
  margin: 0 auto;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 990px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 768px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 575px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 100%;
    padding: 0 15px;
  `}
`;

const SectionTitle = styled.h2`
  font-size: 46px;
  text-align: center;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    font-size: 40px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    font-size: 36px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    font-size: 32px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    font-size: 28px;
  `}
`;

const SaleContainerStyled = styled(SaleContainer)`
  position: relative;
  width: 1064px;
  margin: 0 auto;
  z-index: 2;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 990px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 768px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 575px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 100%;
    padding: 0 15px;
  `}
`;

const SectionTitleStyled = styled(SectionTitle)<{ isEmpty?: boolean; editable?: boolean }>`
  text-align: center;
  margin: 0 auto;
  margin-bottom: 26px;
  width: 100%;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const Description = styled.div<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $subheadlineFont } }) => $subheadlineFont};
  line-height: 1.45em;
  color: ${COLOR_GRAY_13};
  width: 80%;
  text-align: center;
  margin: 0 auto;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
    font-size: 17px;
  `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const EventInner = styled.div`
  display: flex;
  align-items: center;
  padding: 30px 0px;
  position: relative;
  margin-top: 50px;
  justify-content: center;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    margin-top: 36px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    flex-direction: column;
    margin-top: 0px;
  `}
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  justify-content: center;
  position: relative;
  z-index: 3;
  margin-left: 100px;
  max-width: 55%;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    margin-left: 50px;
    max-width: 58%;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    max-width: 100%;
    width: 100%;
    padding-right: 0px;
    padding-bottom: 10px;
    margin-left: 0px;
  `}
`;

const Icon = styled.div`
  width: 178px;
  height: 178px;
  border-radius: 50%;
  filter: drop-shadow(0px 4px 16.6px rgba(0, 0, 0, 0.07));
  background: #fff;
  padding: 40px;
  position: relative;
  z-index: 3;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 148px;
    height: 148px;
    margin-bottom: 25px;
  `}
`;

const WrapperInfo = styled.div`
  display: flex;
  align-items: center;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: column;

    &:not(:last-child) {
      margin-bottom: 14px;
    }
  `}
`;

const InfoTitle = styled.div<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $subheadlineFont } }) => $subheadlineFont};
  min-width: 90px;
  word-break: break-all;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    font-size: 17px;
    text-align: center;
    width: 100%;
    margin-bottom: 6px;
  `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const ItemInfo = styled.div`
  background: #fff;
  border-radius: 20px;
  display: flex;
  padding: 8px 20px;
  border-bottom: 1px solid #dedede;
  margin-left: 10px;
  word-break: break-all;
  max-width: 86%;
  align-items: center;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    max-width: 79%;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    max-width: 84%;
    margin-left: 0px;
  `}

  svg {
    width: 24px;
    height: 26px;
    display: block;
    margin-right: 16px;
    flex-shrink: 0;
  }
`;

const InfoText = styled.div<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $subheadlineFont } }) => $subheadlineFont};
  width: 90%;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const BgEventInfo = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: #fff;
  opacity: 0.75;
  border-radius: 10px;
  z-index: 2;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    top: auto;
    bottom: 0;
    height: 80%;
  `}
`;

export const V2 = (props: EventProps) => {
  const {
    content = defaultWebValue,
    title = defaultWebValue,
    textColor,
    backgroundColor = '#fff',
    benefits = [],
    meta,
    hidden,
    customBackgroundColor,
    oppositeBackgroundColor,
    order
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();

  const { setIsEventFormModalOpen, isDashboard } = useSales();

  // Accessibility improvements
  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');
  const { checkBenefits: checkTitleBenefits } = useIsEmpty(meta, 'title', true);
  const { checkBenefits: checkContentBenefits } = useIsEmpty(meta, 'content', true);

  const resolvedBackgroundColor = useMemo(() => {
    return customBackgroundColor || shade(backgroundColor, 0.7);
  }, [customBackgroundColor, backgroundColor]);

  const finalTextColor = useMemo(
    () => filterBackgroundColor(resolvedBackgroundColor, textColor),
    [resolvedBackgroundColor, textColor]
  );

  const iconColor = useMemo(() => {
    const bg = meta['background_color'];

    if (bg?.includes('background_color4') || bg?.includes('base_color')) return '#000';
    if (bg?.includes('faded_color_')) return oppositeBackgroundColor || backgroundColor;
    return customBackgroundColor || backgroundColor;
  }, [meta, oppositeBackgroundColor, backgroundColor, customBackgroundColor]);

  const titleAccessKey = generateAccessKey('t', order);
  const contentAccessKey = generateAccessKey('c', order);
  const skipLinkId = `skip-to-event-v2-${meta['accessorKey'] || 'event'}`;

  const handleModalError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Event form error: ${error instanceof Error ? error.message : 'Action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  return (
    <Container
      className={containerClassname}
      aria-label="Event information section"
      role="region"
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1,
        backgroundColor: resolvedBackgroundColor
      }}>
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to event information</a>
      </span>

      <SaleContainerStyled id={skipLinkId}>
        <SectionTitleStyled
          id={title.id}
          dangerouslySetInnerHTML={{
            __html: sanitizeRichText(title.val, process.env['APP_TYPE'] === 'dashboard')
          }}
          style={{ color: finalTextColor }}
          isEmpty={isTitleEmpty}
          {...(isDashboard
            ? {
                onClick: () => {
                  try {
                    if (editable) setIsEventFormModalOpen(true);
                  } catch (error) {
                    handleModalError(error);
                  }
                },
                onBlur: resetActiveElement,
                'data-accessor-parent': meta['accessorKey'],
                'data-accessorkey': 'title',
                'data-placeholder': '+',
                editable: true
              }
            : {
                'aria-label': isTitleEmpty || !title.val?.trim() ? 'Event title' : undefined,
                accessKey: titleAccessKey,
                title: `Press Alt+${titleAccessKey} to focus this event title`,
                tabIndex: 0
              })}
        />

        <Description
          id={content.id}
          dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
          style={{ color: finalTextColor }}
          isEmpty={isContentEmpty}
          {...(isDashboard
            ? {
                onClick: updateActiveElement,
                onBlur: resetActiveElement,
                'data-accessor-parent': meta['accessorKey'],
                'data-accessorkey': 'content',
                'data-placeholder': '+',
                editable: true
              }
            : {
                role: 'textbox',
                'aria-multiline': 'true',
                'aria-label':
                  isContentEmpty || !content.val?.trim() ? 'Event description' : undefined,
                accessKey: contentAccessKey,
                title: `Press Alt+${contentAccessKey} to focus this event description`,
                tabIndex: 0
              })}
        />

        <EventInner>
          <Icon role="img" aria-label="Event calendar icon" aria-hidden="false">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 95.68 95.63"
              aria-hidden="true"
              focusable="false">
              <path
                d="m95.63,16.11c-.5-4.7-3.93-8.54-8.54-9.08-2.78-.31-5.61-.14-8.4-.18v9.86c0,2.24-1.46,3.74-3.47,3.74s-3.38-1.55-3.38-3.74V6.85h6.71s.09,0,.14,0v-.05l-.09.03v-3.41c-.05-1.92-1.51-3.42-3.38-3.42-1.92-.05-3.47,1.46-3.47,3.42v3.38H23.96v9.91c0,2.24-1.46,3.74-3.47,3.74s-3.38-1.55-3.38-3.74V6.85h6.8v-3.42C23.87,1.51,22.41,0,20.54,0s-3.38,1.46-3.42,3.42v3.38h-6.62c-6.03.05-10.5,4.47-10.5,10.45v9.08c0,.32.05.64.05.91h95.58v-9.77c0-.46.05-.91,0-1.37Z"
                fill={iconColor || '#2EC5CE'}
                strokeWidth={0}
              />
              <path
                d="m0,34.24v50.8c0,6.16,4.47,10.59,10.59,10.59h74.54c1,0,2.01-.09,2.97-.37,4.56-1.23,7.58-5.16,7.58-10v-51.03H0Zm70.28,25.7l-10.52,7.64c-.7.51-.99,1.41-.73,2.24l4.02,12.37c.6,1.84-1.51,3.37-3.08,2.24l-10.52-7.64c-.7-.51-1.65-.51-2.35,0l-10.52,7.64c-1.57,1.14-3.68-.39-3.08-2.24l4.02-12.37c.27-.82-.03-1.73-.73-2.24l-10.52-7.64c-1.57-1.14-.76-3.62,1.18-3.62h13c.87,0,1.63-.56,1.9-1.38l4.02-12.37c.6-1.84,3.21-1.84,3.8,0l4.02,12.37c.27.82,1.04,1.38,1.9,1.38h13c1.94,0,2.74,2.48,1.18,3.62Z"
                fill={iconColor || '#2EC5CE'}
                strokeWidth={0}
              />
              <path
                d="m0,34.24v50.8c0,6.16,4.47,10.59,10.59,10.59h74.54c1,0,2.01-.09,2.97-.37,4.56-1.23,7.58-5.16,7.58-10v-51.03H0Zm70.28,25.7l-10.52,7.64c-.7.51-.99,1.41-.73,2.24l4.02,12.37c.6,1.84-1.51,3.37-3.08,2.24l-10.52-7.64c-.7-.51-1.65-.51-2.35,0l-10.52,7.64c-1.57,1.14-3.68-.39-3.08-2.24l4.02-12.37c.27-.82-.03-1.73-.73-2.24l-10.52-7.64c-1.57-1.14-.76-3.62,1.18-3.62h13c.87,0,1.63-.56,1.9-1.38l4.02-12.37c.6-1.84,3.21-1.84,3.8,0l4.02,12.37c.27.82,1.04,1.38,1.9,1.38h13c1.94,0,2.74,2.48,1.18,3.62Z"
                opacity={0.54}
                fill={'#fff'}
                strokeWidth={0}
              />
            </svg>
          </Icon>

          <Info role="list" aria-label="Event details">
            {benefits?.map(({ title: benefitTitle, content: benefitContent }, index) => {
              const isTitleEmpty = checkTitleBenefits(index);
              const isContentEmpty = checkContentBenefits(index);
              const titleAccessKey = generateAccessKey('et', index);
              const contentAccessKey = generateAccessKey('ec', index);

              // Determine icon type and label
              const getIconInfo: () => {
                iconName: 'link' | 'time' | 'date' | 'pin';
                label: string;
              } = () => {
                const titleVal = benefitTitle?.val;
                if (titleVal === 'Date:') return { iconName: 'date', label: 'Date icon' };
                if (titleVal === 'Time:') return { iconName: 'time', label: 'Time icon' };
                if (titleVal === 'Place:' && benefitContent?.val === 'Online')
                  return { iconName: 'link', label: 'Online event icon' };
                if (meta?.['isEventOnline'] === 'local' && titleVal === 'Place:')
                  return { iconName: 'pin', label: 'Location icon' };
                return { iconName: 'link', label: 'Link icon' };
              };

              const { iconName, label } = getIconInfo();

              return (
                <WrapperInfo key={index} role="listitem" aria-label={`Event detail ${index + 1}`}>
                  <InfoTitle
                    id={benefitTitle?.id}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeRichText(benefitTitle?.val)
                    }}
                    style={{ color: filterBackgroundColor('#FFF', textColor) }}
                    isEmpty={isTitleEmpty}
                    {...(isDashboard
                      ? {
                          onClick: () => {
                            try {
                              if (editable) setIsEventFormModalOpen(true);
                            } catch (error) {
                              handleModalError(error);
                            }
                          },
                          onBlur: resetActiveElement,
                          'data-accessor-parent': meta['accessorKey'],
                          'data-accessorkey': 'title',
                          'data-benefits': true,
                          'data-placeholder': '+',
                          editable: true
                        }
                      : {
                          'aria-label':
                            isTitleEmpty || !benefitTitle?.val?.trim()
                              ? `Event detail ${index + 1} label`
                              : undefined,
                          accessKey: titleAccessKey,
                          title: `Press Alt+${titleAccessKey} to focus this event detail label`,
                          tabIndex: 0
                        })}
                  />

                  <ItemInfo
                    role="group"
                    aria-label={`${benefitTitle?.val || `Detail ${index + 1}`} information`}>
                    <div role="img" aria-label={label} aria-hidden="false">
                      <SvgIcons
                        iconName={iconName}
                        backgroundColor={iconColor || '#2EC5CE'}
                        sizeWidth="30px"
                        sizeHeight="30px"
                        aria-hidden="true"
                      />
                    </div>

                    <InfoText
                      id={benefitContent?.id}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichText(benefitContent?.val, editable)
                      }}
                      style={{ color: filterBackgroundColor('#fff', textColor) }}
                      isEmpty={isContentEmpty}
                      {...(isDashboard
                        ? {
                            onClick: () => {
                              try {
                                if (editable) setIsEventFormModalOpen(true);
                              } catch (error) {
                                handleModalError(error);
                              }
                            },
                            onBlur: resetActiveElement,
                            'data-accessor-parent': meta['accessorKey'],
                            'data-accessorkey': 'content',
                            'data-benefits': true,
                            'data-placeholder': '+',
                            editable: true
                          }
                        : {
                            role: 'textbox',
                            'aria-multiline': 'true',
                            'aria-label': `${benefitTitle?.val || `Detail ${index + 1}`} value`,
                            accessKey: contentAccessKey,
                            title: `Press Alt+${contentAccessKey} to focus this event detail value`,
                            tabIndex: 0
                          })}
                    />
                  </ItemInfo>
                </WrapperInfo>
              );
            })}
          </Info>

          <BgEventInfo aria-hidden="true" />
        </EventInner>
      </SaleContainerStyled>
    </Container>
  );
};

V2.displayName = 'EventsV2';
